import type { DataEditorRef, Item } from "@artis/data-grid";
import { type RefObject, useEffect, useRef, useState } from "react";

import FormulaInput, { type CommitMovement, type FormulaInputHandle } from "./FormulaInput";

const CHROME = {
  borderRadius: "3px",
  background: "var(--artis-cell-editor-background)",
  color: "var(--colour-text-default)",
  boxShadow: "0 0 0 1px var(--artis-cell-editor-border), var(--artis-cell-editor-shadow)",
};

const CLIP_PAD = 20;
/** The grid lines framing a cell are its neighbours' right and bottom pixels. */
const FRAME_OFFSET = 1;

interface CellEditorProps {
  gridRef: RefObject<DataEditorRef | null>;
  cell: Item;
  text: string;
  inputRef?: (el: FormulaInputHandle | null) => void;
  onChange?: (value: string) => void;
  onCaretChange?: (caret: number) => void;
  onCommit?: (movement?: CommitMovement) => void;
  onCancel?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

export default function CellEditor({ gridRef, cell, text, ...input }: CellEditorProps) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const [initial] = useState(() => gridRef.current?.getBounds(cell[0], cell[1]));

  useEffect(() => {
    let frame = 0;
    const follow = () => {
      const el = elRef.current;
      const grid = gridRef.current;
      const bounds = grid?.getBounds(cell[0], cell[1]);
      if (el) {
        if (bounds) {
          const marker = grid?.getBounds(-1, cell[1]);
          const header = grid?.getBounds(cell[0], -1);
          const left = bounds.x - FRAME_OFFSET;
          const top = bounds.y - FRAME_OFFSET;
          const clipLeft = marker ? Math.max(0, marker.x + marker.width - left) : 0;
          const clipTop = header ? Math.max(0, header.y + header.height - top) : 0;
          el.style.display = "";
          el.style.left = `${left}px`;
          el.style.top = `${top}px`;
          el.style.minWidth = `${bounds.width}px`;
          el.style.height = `${bounds.height}px`;
          el.style.clipPath =
            clipLeft || clipTop
              ? `inset(${clipTop || -CLIP_PAD}px ${-CLIP_PAD}px ${-CLIP_PAD}px ${clipLeft || -CLIP_PAD}px)`
              : "";
        } else {
          el.style.display = "none";
        }
      }
      frame = requestAnimationFrame(follow);
    };
    frame = requestAnimationFrame(follow);
    return () => cancelAnimationFrame(frame);
  }, [gridRef, cell]);

  return (
    <div
      ref={elRef}
      data-cell-editor
      data-artis-ui
      data-testid="cell-editor"
      className="fixed z-10 flex w-max max-w-[50vw] items-stretch"
      style={{
        display: initial ? "" : "none",
        left: initial && initial.x - FRAME_OFFSET,
        top: initial && initial.y - FRAME_OFFSET,
        minWidth: initial?.width,
        height: initial?.height,
        ...CHROME,
      }}
    >
      <FormulaInput className="min-w-0 flex-1 text-[12px]" value={text} {...input} />
    </div>
  );
}
